import { Fragment, useState } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Tag } from "@impero-com/ui";

import { WebRoute } from "routes";
import { useIsClamped } from "utils/hooks/useIsClamped";

import * as theme from "./EntitiesCell.css";
import { EntitiesDrawer } from "./EntitiesDrawer";
import { EntitiesCellEntity } from "./types";

const MAX_ENTITIES = 30;

interface EntitiesCellProps {
    entities: Array<EntitiesCellEntity>;
}

export function EntitiesCell({ entities }: EntitiesCellProps) {
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    // The drawer is mounted only when the user clicks on the pill,
    // so that it doesn't render unnecessarily when the cell is not clamped
    const [drawerMounted, setDrawerMounted] = useState(false);

    const { ref: namesRef, isClamped } = useIsClamped();

    const openDrawer = () => {
        setDrawerMounted(true);
        setDrawerOpen(true);
    };

    if (entities.length === 0) {
        return null;
    }

    return (
        <div className={theme.cell}>
            {/* The span is used to measure whether the names are clamped, so that we can show the pill if they are. */}
            <span ref={namesRef} className={theme.names}>
                {entities.slice(0, MAX_ENTITIES).map((entity, index) => (
                    <Fragment key={entity.id}>
                        {index > 0 && ", "}
                        <ProtectedLink
                            href={`${WebRoute.ENTITIES}/${entity.id}`}
                            hasAccess={true}
                        >
                            {entity.name}
                        </ProtectedLink>
                    </Fragment>
                ))}
            </span>
            {(isClamped || entities.length > MAX_ENTITIES) && (
                <Tag
                    variant="countPill"
                    onClick={openDrawer}
                    aria-label={t("ControlPrograms_EntitiesCell_ShowAll")}
                >
                    {entities.length}
                </Tag>
            )}
            {drawerMounted && (
                <EntitiesDrawer
                    entities={entities}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                />
            )}
        </div>
    );
}
